import { useState } from 'react'
import { Card, CardContent, Chip, Avatar, Typography } from '@mui/material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNotification } from '../../context/NotificationManager/NotificationManager'
import PageLoading from '../../components/PageLoading/PageLoading'
import TextLabel from '../../components/form/TextLabel/TextLabel'
import ModalFotoPerfil from '../../modals/ModalFotoPerfil/ModalFotoPerfil'
import { getProfesor, updateFotoPerfil } from '../../utils/api/profesores'
import { dateToStringLong, getDateFromObjectId } from '../../utils/date'
import { updateAuthFotoPerfil } from '../../utils/auth'
import { makeUniqeUrl } from '../../utils/request'
import css from './Perfil.module.css'

const AsignaturaIcon = ({ tipo }) => (
  <i className={`${css.icon} material-icons`}>
    {tipo === 'CIENCIAS' && 'science'}
    {tipo === 'CIENCIAS_SALUD' && 'biotech'}
    {tipo === 'ARTES' && 'palette'}
    {tipo === 'IDIOMAS' && 'translate'}
    {tipo === 'LETRAS' && 'history_edu'}
    {tipo === 'OTROS' && 'school'}
  </i>
)

const asignaturaLabel = ({ nombre, nivel, subnivel }) => {
  if (subnivel) return `${nombre} (${nivel} - ${subnivel})`
  return `${nombre} (${nivel})`
}

const Perfil = () => {
  const [isOpenFotoPerfil, setIsOpenFotoPerfil] = useState(false)
  const notification = useNotification()
  const queryClient = useQueryClient()

  const { isLoading, data: perfil = {} } = useQuery({
    queryKey: ['personal', 'profesor'],
    queryFn: () =>
      getProfesor()
        .then(datos => {
          const imagen = makeUniqeUrl(datos.imagen)
          updateAuthFotoPerfil(imagen)
          return { ...datos, imagen, fecha: getDateFromObjectId(datos.id) }
        })
        .catch(err => {
          notification.error({
            title: 'Error al recuperar tu perfil',
            content: err
          })
          return {}
        })
  })

  const { isPending: isFotoPerfilUpdating, mutate: actualizarFotoPerfil } =
    useMutation({
      mutationFn: updateFotoPerfil,
      onSuccess: () => {
        notification.success({
          title: 'Foto de perfil actualizada',
          content: 'Se ha actualizado tu foto de perfil'
        })
        queryClient.invalidateQueries({ queryKey: ['personal', 'profesor'] })
        setIsOpenFotoPerfil(false)
      },
      onError: err => {
        notification.error({
          title: 'Error al actualizar la foto de perfil',
          content: err
        })
      }
    })

  const subirFotoPerfil = file => {
    if (isFotoPerfilUpdating) return
    actualizarFotoPerfil({ id: perfil.id, foto: file })
  }

  const hasAsignaturas = perfil.asignaturas && perfil.asignaturas.length > 0

  return (
    <PageLoading isLoading={isLoading}>
      <div className={css.main}>
        <Typography variant='h5' className={css.title}>
          Mi perfil
        </Typography>
        <div className={css.content}>
          <Card className={css.card}>
            <CardContent className={css.cardContent}>
              <div className={css.header}>
                <Typography variant='h6'>Mis datos</Typography>
              </div>
              <div className={css.profileSection}>
                <Avatar
                  className={css.profileAvatar}
                  alt={perfil.nombre}
                  src={perfil.imagen}
                  onClick={() => setIsOpenFotoPerfil(true)}
                />
                <div className={css.profileInfo}>
                  <TextLabel
                    className={css.input}
                    label='Nombre'
                    disabled
                    value={perfil.nombre}
                  />
                  <TextLabel
                    className={css.input}
                    label='Email'
                    disabled
                    value={perfil.email}
                  />
                </div>
              </div>
              <TextLabel
                className={css.input}
                label='Fecha de registro'
                disabled
                value={dateToStringLong(perfil.fecha)}
              />
            </CardContent>
          </Card>
          <Card className={css.card}>
            <CardContent className={css.cardContent}>
              <Typography variant='h6' className={css.cardTitle}>
                Mis asignaturas
              </Typography>
              {hasAsignaturas ? (
                <div className={css.asignaturas}>
                  {perfil.asignaturas
                    .sort((a, b) => a.nombre.localeCompare(b.nombre))
                    .map(asignatura => (
                      <Chip
                        key={asignatura.id}
                        className={css.chip}
                        data-status={asignatura.activa ? 'active' : 'inactive'}
                        icon={<AsignaturaIcon tipo={asignatura.tipo} />}
                        label={asignaturaLabel(asignatura)}
                      />
                    ))}
                </div>
              ) : (
                <Typography className={css.placeholder} variant='body2'>
                  No tienes asignaturas asignadas
                </Typography>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      <ModalFotoPerfil
        open={isOpenFotoPerfil}
        foto={perfil.imagen}
        isLoading={isFotoPerfilUpdating}
        onClose={() => setIsOpenFotoPerfil(false)}
        onSubmit={subirFotoPerfil}
      />
    </PageLoading>
  )
}

export default Perfil
